import { useEffect, useState, useCallback, useRef } from "react"
import { io, Socket } from "socket.io-client"
import TTSService from "../services/ttsService"
import { useSelector } from "react-redux"
import { selectChunkData } from "../slice/aiInterviewSlice"

const SOCKET_URL = "https://api.peoplecq.com/ai-interviewer" // ENABLE ON PROD
// const SOCKET_URL = "https://dev.api.workimize.com/ai-interviewer"
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_KEY as string

// Initialize TTS Service with OpenAI
const ttsService = new TTSService({
  useOpenAI: true,
  apiKey: OPENAI_API_KEY,
  voice: 'nova', // High quality female voice
  model: 'tts-1'
})
//releasing
interface Message {
  id: string
  content: string
  from: 'user' | 'ai'
  timestamp: number
}

interface SocketError {
  message: string
  timestamp: number
  type?: string
}

interface UseSocketInterviewReturn {
  socket: Socket | null
  connected: boolean
  sessionId: string | null
  messages: Message[]
  isAiTyping: boolean
  isMyraSpeaking: boolean
  error: SocketError | null
  aiEndCall: boolean
  sendMessage: (content: string) => void
  sendUserEndCall: () => void
  // sendRecordingChunk: (chunkIndex: number, chunk: string) => void
  disconnect: () => void
  clearError: () => void
  stopTTS: () => void
}

export const useSocketInterview = (): UseSocketInterviewReturn => {
  // Use ref for socket to prevent re-renders and maintain single connection
  const socketRef = useRef<Socket | null>(null)
  
  // Audio element ref for playing buffer from backend
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  
  // Use state only for UI-related properties
  const [connected, setConnected] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [isAiTyping, setIsAiTyping] = useState(false)
  const [isMyraSpeaking, setIsMyraSpeaking] = useState(false)
  const [error, setError] = useState<SocketError | null>(null)
  const [aiEndCall, setAiEndCall] = useState(false)
  const selectedChunkData = useSelector(selectChunkData)
  
  // Use ref to always get the latest chunkData value
  const selectedChunkDataRef = useRef(selectedChunkData)

  // Use ref to store invitationId - won't cause re-renders
  const invitationIdRef = useRef(localStorage.getItem('invitationId'))
  
  // Update ref whenever selectedChunkData changes
  useEffect(() => {
    selectedChunkDataRef.current = selectedChunkData
  }, [selectedChunkData])
  useEffect(() => {
    // Prevent multiple socket connections
    if (socketRef.current) {
      return
    }
   
    // Helper function to play audio buffer from backend
    const playBackendAudio = async (
      audioBuffer: any,
      onStart?: () => void,
      onEnd?: () => void
    ) => {
      try {
        
        // Stop any existing audio
        if (audioElementRef.current) {
          audioElementRef.current.pause()
          audioElementRef.current.src = ''
          audioElementRef.current = null
        }

        // Create blob from buffer
        const blob = new Blob([audioBuffer], { type: 'audio/mpeg' })
        
        const blobUrl = URL.createObjectURL(blob)

        // Create audio element
        const audio = new Audio()
        audioElementRef.current = audio
        
        audio.onloadedmetadata = () => {
        }
        
        audio.onplay = () => {
          onStart?.()
        }

        audio.onended = () => {
          URL.revokeObjectURL(blobUrl)
          audioElementRef.current = null
          onEnd?.()
        }

        audio.onerror = (e) => {
          URL.revokeObjectURL(blobUrl)
          audioElementRef.current = null
          onEnd?.()
        }
        
        audio.src = blobUrl
        await audio.play()
        
      } catch (error) {
        audioElementRef.current = null
        onEnd?.()
      }
    }
    
    const newSocket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['websocket'], 
      reconnection: false,
      reconnectionAttempts: 5,
      timeout: 20000,
    })

    // Store socket in ref immediately to prevent duplicate connections
    socketRef.current = newSocket

    newSocket.on("connect", () => {

      setConnected(true)
      
      // Show typing indicator immediately - AI will send first message
      setIsAiTyping(true)
      
      // Join the interview session with invitation ID
      newSocket.emit('join', {
        interviewInvitationId: invitationIdRef.current
      })
    })

    // Tag: interview-ready
   

    // Tag: ai-session-created
    newSocket.on('ai-session-created', (data: any) => {
      if (data?.sessionId) {
        setSessionId(data.sessionId)
      }
    })

   

   

    // NEW: Handle ai-reply-text from backend
    newSocket.on('ai-reply-text', (data: any) => {
      
      
      if (data?.message) {
        // Hide typing indicator and add message to UI
        setIsAiTyping(false)
        const aiMessage: Message = {
          id: `ai-${Date.now()}`,
          content: data.message,
          from: 'ai',
          timestamp: Date.now()
        }
        setMessages(prev => [...prev, aiMessage])
      }
    })

    // NEW: Handle ai-reply-audio from backend (Buffer)
    newSocket.on('ai-reply-audio', async (audioBuffer: any) => {
      if (audioBuffer) {
        // Play audio buffer from backend
        await playBackendAudio(
          audioBuffer,
          () => {
            setIsMyraSpeaking(true)
          },
          () => {
            setIsMyraSpeaking(false)
          }
        )
      } else {
      }
    })

    // Tag: ai-end-call - AI ends the interview
    newSocket.on('ai-end-interview', () => {
      setAiEndCall(true)
    })

    // Handle socket errors
    newSocket.on('error', (errorData: any) => {
      const errorMessage = errorData?.message || errorData?.error || 'An error occurred'
      setError({
        message: errorMessage,
        timestamp: Date.now(),
        type: errorData?.type || 'socket_error'
      })
      setIsAiTyping(false)
    })

    newSocket.on("disconnect", (reason) => {
      setConnected(false)
      if (reason !== 'io client disconnect') {
        setError({
          message: 'Connection lost',
          timestamp: Date.now(),
          type: 'disconnect'
        })
      }
    })

    newSocket.on("connect_error", (error) => {
      setError({
        message: error?.message || 'Failed to connect to server',
        timestamp: Date.now(),
        type: 'connect_error'
      })
    })

    return () => {
      // Cleanup audio on unmount
      if (audioElementRef.current) {
        audioElementRef.current.pause()
        audioElementRef.current.src = ''
        audioElementRef.current = null
      }
      newSocket.disconnect()
      socketRef.current = null
    }
  }, []) // ← Empty array = runs only ONCE on mount!

  const sendMessage = useCallback((content: string) => {
    if (!content.trim() || !socketRef.current || !connected) return

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      content: content,
      from: 'user',
      timestamp: Date.now()
    }
    setMessages(prev => [...prev, userMsg])

    // Tag: user-reply
    socketRef.current.emit('user-reply', {
      message: content
    })

    setIsAiTyping(true)
  }, [connected])

  const sendUserEndCall = useCallback(() => {
    if (!socketRef.current || !connected) return

    // Tag: user-end-call
    socketRef.current.emit('end-interview', {
      timestamp: Date.now()
    })
  }, [connected])


  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
      setConnected(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const stopTTS = useCallback(() => {
    // Stop OpenAI TTS
    ttsService.stop()
    
    // Stop backend audio if playing
    if (audioElementRef.current) {
      try {
        audioElementRef.current.pause()
        audioElementRef.current.src = ''
        audioElementRef.current = null
      } catch (e) {
      }
    }
    
    setIsMyraSpeaking(false)
  }, [])

  return {
    socket: socketRef.current,
    connected,
    sessionId,
    messages,
    isAiTyping,
    isMyraSpeaking,
    error,
    aiEndCall,
    sendMessage,
    sendUserEndCall,
    // sendRecordingChunk,
    disconnect,
    clearError,
    stopTTS
  }
}

// Text-to-Speech helper with OpenAI TTS

